/**
 * Contexto de página para los `$pageview` de PostHog.
 *
 * Las páginas de edificio (multifamily) cambian de estado sin recargar: el
 * cambio de tipología reescribe el pathname y la selección de unidad sólo
 * toca `?unit_id=`. Con `capture_pageview: 'history_change'` PostHog envía
 * `$pageview` únicamente cuando cambia el pathname, y sin propiedades que
 * describan qué se está viendo.
 *
 * Este módulo agrega un `before_send` que enriquece sólo los `$pageview`
 * con el contexto declarado por la página. El hook se instala únicamente
 * cuando la página declara contexto (bloque JSON renderizado por Blade), así
 * el resto del sitio no pasa por él.
 *
 * Sin imports de PostHog ni DOM global: el cliente y el document se inyectan
 * para poder testearlo en Node.
 */

export const PAGEVIEW_EVENT = '$pageview';
export const PAGEVIEW_CONTEXT_SELECTOR = 'script[data-pageview-context]';

export function createPageviewContext() {
    let context = null;
    let client = null;

    function beforeSend(event) {
        if (!event || event.event !== PAGEVIEW_EVENT || context === null) {
            return event;
        }

        return {
            ...event,
            properties: { ...event.properties, ...context },
        };
    }

    return {
        beforeSend,

        get() {
            return context === null ? null : { ...context };
        },

        set(nextContext) {
            context = { ...nextContext };
        },

        merge(partialContext) {
            context = { ...(context ?? {}), ...partialContext };
        },

        /**
         * Encadena `beforeSend` a los `before_send` que ya tenga el cliente.
         * Idempotente: una segunda llamada no vuelve a registrar el hook.
         */
        attach(posthogClient) {
            if (client !== null || !posthogClient?.set_config) {
                return;
            }

            const current = posthogClient.config?.before_send;
            const chain = current == null ? [] : [].concat(current);

            posthogClient.set_config({ before_send: [...chain, beforeSend] });
            client = posthogClient;
        },

        /**
         * `$pageview` manual para cambios de estado que no alteran el pathname
         * (p. ej. seleccionar una unidad). Pasa por `beforeSend` como cualquier
         * otro pageview, por lo que llevará el contexto vigente.
         */
        capturePageview() {
            client?.capture(PAGEVIEW_EVENT);
        },
    };
}

/**
 * Lee el contexto inicial que la página serializó en
 * `<script type="application/json" data-pageview-context>`.
 */
export function readPageviewContext(documentRef) {
    const element = documentRef?.querySelector?.(PAGEVIEW_CONTEXT_SELECTOR);

    if (!element) {
        return null;
    }

    try {
        const parsed = JSON.parse(element.textContent);

        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
    } catch {
        return null;
    }
}

/**
 * Instala el contexto sólo si la página lo declaró. Debe llamarse justo
 * después de `posthog.init`: el `$pageview` inicial se captura en un
 * `setTimeout(…, 1)` posterior, por lo que alcanza a pasar por el hook.
 */
export function bootPageviewContext(pageviewContext, { document: documentRef, client }) {
    const initialContext = readPageviewContext(documentRef);

    if (initialContext === null) {
        return false;
    }

    // Lo que la página ya actualizó en runtime (Alpine puede iniciar antes)
    // prevalece sobre el valor renderizado por el servidor.
    pageviewContext.set({ ...initialContext, ...(pageviewContext.get() ?? {}) });
    pageviewContext.attach(client);

    return true;
}

/**
 * Cliente mínimo para desarrollo: en vez de enviar a PostHog, loguea el
 * `$pageview` con el contexto que llevaría.
 */
export function createConsolePageviewClient(pageviewContext, logger = console) {
    return {
        config: {},
        set_config() {},
        capture(eventName) {
            const event = pageviewContext.beforeSend({ event: eventName, properties: {} });
            logger.log('📄 Pageview:', event?.properties ?? {});
        },
    };
}

export const pageviewContext = createPageviewContext();
